import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiClient } from '@/api/client'

export interface QuickLink {
  id: number
  label: string
  url: string
  icon_url: string | null
  is_internal: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface QuickLinkInput {
  label: string
  url: string
  refresh_icon?: boolean
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
  errors?: Record<string, string>
}

/** Icône : lien interne → icône Capatools ; sinon icône résolue côté serveur ; chaîne vide si aucune. */
export function quickLinkIconUrl(link: Pick<QuickLink, 'is_internal'> & { icon_url?: string | null }): string {
  if (link.is_internal) return '/capaicon/favicon-32x32.png'
  return link.icon_url || ''
}

export const useQuickLinksStore = defineStore('quickLinks', () => {
  const links = ref<QuickLink[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const loaded = ref(false)

  /** Liens du header, pour tous les utilisateurs. */
  async function loadActiveLinks() {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiClient.get<ApiResponse<{ links: QuickLink[] }>>('/api/quick-links')
      if (response.success) {
        links.value = response.data.links
        loaded.value = true
      }
    } catch (e) {
      error.value = 'Erreur lors du chargement des liens rapides'
      console.error('Error loading quick links:', e)
    } finally {
      isLoading.value = false
    }
  }

  /** Tous les liens (page admin). */
  async function loadAllLinks() {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiClient.get<ApiResponse<{ links: QuickLink[] }>>('/api/admin/quick-links')
      if (response.success) {
        links.value = response.data.links
      }
    } catch (e) {
      error.value = 'Erreur lors du chargement des liens rapides'
      console.error('Error loading quick links:', e)
    } finally {
      isLoading.value = false
    }
  }

  async function createLink(data: QuickLinkInput): Promise<QuickLink> {
    const response = await apiClient.post<ApiResponse<{ link: QuickLink }>>('/api/admin/quick-links', data)
    if (!response.success) throw new Error(response.message || 'Erreur lors de la création')
    links.value.push(response.data.link)
    return response.data.link
  }

  async function updateLink(id: number, data: Partial<QuickLinkInput>): Promise<QuickLink> {
    const response = await apiClient.put<ApiResponse<{ link: QuickLink }>>(`/api/admin/quick-links/${id}`, data)
    if (!response.success) throw new Error(response.message || 'Erreur lors de la mise à jour')
    const index = links.value.findIndex(l => l.id === id)
    if (index !== -1) links.value[index] = response.data.link
    return response.data.link
  }

  async function deleteLink(id: number): Promise<boolean> {
    try {
      const response = await apiClient.delete<ApiResponse<{ message: string }>>(`/api/admin/quick-links/${id}`)
      if (response.success) {
        links.value = links.value.filter(l => l.id !== id)
        return true
      }
      return false
    } catch (e) {
      console.error('Error deleting quick link:', e)
      return false
    }
  }

  /** Aperçu : icône résolue côté serveur pour une URL saisie (null si rien trouvé). */
  async function resolveIcon(url: string): Promise<string | null> {
    try {
      const response = await apiClient.get<ApiResponse<{ icon_url: string | null }>>('/api/admin/quick-links/resolve-icon', { url })
      return response.success ? response.data.icon_url : null
    } catch {
      return null
    }
  }

  /** Persiste l'ordre ; en cas d'échec, recharge la liste depuis le serveur. */
  async function reorder(linkIds: number[]): Promise<boolean> {
    try {
      const response = await apiClient.put<ApiResponse<{ links: QuickLink[] }>>('/api/admin/quick-links/reorder', { linkIds })
      if (response.success) {
        links.value = response.data.links
        return true
      }
    } catch (e) {
      console.error('Error reordering quick links:', e)
    }
    await loadAllLinks()
    return false
  }

  return {
    links,
    isLoading,
    error,
    loaded,
    loadActiveLinks,
    loadAllLinks,
    createLink,
    updateLink,
    deleteLink,
    resolveIcon,
    reorder,
  }
})
